import React, { useMemo } from 'react';
import {
  Card,
  Form,
  Input,
  Select,
  DatePicker,
  Button,
  Space,
  Row,
  Col,
  Tooltip,
} from 'antd';
import { PlusOutlined, InfoCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import AddCustomerModal from '../modals/AddCustomerModal';
import CustomerAutocomplete from '../CustomerAutocomplete';

const ProposalHeader = ({
  formData,
  customers,
  languages,
  isEdit,
  onInputChange,
  onNewCustomer,
  showNewCustomerModal,
  setShowNewCustomerModal,
  onCustomerSelect,
  customerError = null,
}) => {
  const emitChange = (name, value) => {
    onInputChange({ target: { name, value } });
  };

  const selectedCustomer = customers.find(
    (c) => c.person_id === formData.prospect_id
  );
  const customerDisplay = selectedCustomer
    ? `${selectedCustomer.first_name} ${selectedCustomer.last_name}`
    : '';

  // Normalize IDs to numbers so Select can match option values and show labels
  const languageOptions = useMemo(
    () =>
      (languages || []).map((l) => ({
        value: Number(l.language_id),
        label: l.name || l.title || l.code || String(l.language_id),
      })),
    [languages]
  );

  const selectedLanguageId = Number(formData.language_id ?? 1);

  return (
    <Card
      title="Proposal details"
      data-testid="proposal-header"
      style={{ marginBottom: 24 }}
      styles={{ body: { paddingBottom: 8 } }}
    >
      <Form layout="vertical">
        <Form.Item
          label="Proposal name"
          style={{ marginBottom: 20 }}
        >
          <Input
            id="name"
            name="name"
            size="large"
            value={formData.name || ''}
            onChange={onInputChange}
            placeholder="e.g. Kitchen renovation, Living room set"
            data-testid="proposal-name"
            autoFocus={!isEdit}
          />
        </Form.Item>

        <Form.Item
          label={
            <Space size={8}>
              <span>Customer</span>
              {!isEdit && (
                <Button
                  type="link"
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={() => setShowNewCustomerModal(true)}
                  data-testid="add-customer-btn"
                  style={{ padding: 0, height: 'auto' }}
                >
                  New
                </Button>
              )}
            </Space>
          }
          required
          validateStatus={customerError ? 'error' : undefined}
          help={customerError || undefined}
          style={{ marginBottom: 16 }}
        >
          {isEdit ? (
            <Input
              value={customerDisplay}
              disabled
              data-testid="customer-display"
            />
          ) : (
            <CustomerAutocomplete
              selectedCustomerId={formData.prospect_id}
              onSelect={onCustomerSelect}
              customers={customers}
              placeholder="Search customers..."
              data-testid="customer-autocomplete"
            />
          )}
          <input
            type="hidden"
            name="prospect_id"
            value={formData.prospect_id || ''}
            required
          />
        </Form.Item>

        <Row gutter={[16, 0]}>
          <Col xs={24} sm={8}>
            <Form.Item label="Valid until" required style={{ marginBottom: 16 }}>
              <div data-testid="valid-until">
                <DatePicker
                  style={{ width: '100%' }}
                  value={
                    formData.valid_until ? dayjs(formData.valid_until) : null
                  }
                  onChange={(date) =>
                    emitChange(
                      'valid_until',
                      date ? date.format('YYYY-MM-DD') : ''
                    )
                  }
                  disabledDate={(current) =>
                    current && current < dayjs().startOf('day')
                  }
                  format="YYYY-MM-DD"
                />
              </div>
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item
              label={
                <Space size={4}>
                  <span>Language</span>
                  <Tooltip title="Affects text and translations only">
                    <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
                  </Tooltip>
                </Space>
              }
              required
              style={{ marginBottom: 16 }}
            >
              <div data-testid="language-select">
                <Select
                  id="language_id"
                  value={
                    languageOptions.some((o) => o.value === selectedLanguageId)
                      ? selectedLanguageId
                      : languageOptions[0]?.value
                  }
                  onChange={(value) => emitChange('language_id', Number(value))}
                  options={languageOptions}
                  optionLabelProp="label"
                  style={{ width: '100%' }}
                />
              </div>
            </Form.Item>
          </Col>
          <Col xs={24} sm={8}>
            <Form.Item
              label={
                <Space size={4}>
                  <span>Currency</span>
                  <Tooltip title="All prices in this proposal use this currency">
                    <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
                  </Tooltip>
                </Space>
              }
              required
              style={{ marginBottom: 16 }}
            >
              <div data-testid="currency-select">
                <Select
                  id="currency_code"
                  value={formData.currency_code}
                  onChange={(value) => emitChange('currency_code', value)}
                  options={[
                    { value: 'CZK', label: 'CZK' },
                    { value: 'EUR', label: 'EUR' },
                  ]}
                  style={{ width: '100%' }}
                />
              </div>
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <AddCustomerModal
        show={showNewCustomerModal}
        onHide={() => setShowNewCustomerModal(false)}
        onCustomerCreated={onNewCustomer}
      />
    </Card>
  );
};

export default ProposalHeader;
